import type { Attachment, Reference } from 'fhir/r4';
import { base64ToPdfBlob } from 'fhir-sdoh/common/constants';
import { fileToBase64 } from 'fhir-sdoh/common/fhir-builders/note';
import moment from 'moment';
import type { ChangeEvent } from 'react';
import { useRef, useState } from 'react';
import { Button, Icon } from 'semantic-ui-react';

import type { QuestionnaireAttachmentProps } from './item-props';

export default function Attachment(props: QuestionnaireAttachmentProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    //if file doesn't exist, set the answer state to null
    //if file exists, check file size, convert to base64 and store as valueAttachment object, which will be convert to valueReference and create document reference later in suvey-proxy
    if (!e.target.files?.[0]) {
      props.addAnswer(null, props.i, props.j);
    } else {
      //maximum file size acceptance is 6MB
      const MAX_SIZE = 6 * 1024 * 1024; // 6MB
      if (file.size > MAX_SIZE) {
        setError('This file exceeds the size limit 6MB and cannot be uploaded.');
        e.target.value = ''; // Clear file input
        return;
      } else {
        setError('');
      }
      //convert file data to base64
      // `let` + `if`, as upstream writes it. A ternary with an `undefined` else-arm DISCARDS the
      // original value when the test fails, where upstream leaves it untouched — divergent on 3
      // of 5 input shapes. Unreachable today because `readAsDataURL` always yields a string, but
      // it becomes live the moment that producer changes.
      let base64: string | ArrayBuffer | null | undefined = await fileToBase64(file);
      if (base64 && typeof base64 === 'string') {
        base64 = base64.split('base64,')[1]; //only need the base64 part, remove the contentType in the beginning
      }

      const attachment: Attachment = {
        contentType: file.type,
        creation: moment().toISOString(),
        data: base64 as string | undefined,
        title: file.name,
      };

      //set image payload as valueAttachment and survey-proxy will convert to valueReference later
      props.addAnswer(
        [
          {
            valueAttachment: attachment,
          },
        ],
        props.i,
        props.j,
      );
    }
    // Reset so user can re-select same file again if needed
    e.target.value = '';
  };

  const handleDelete = () => {
    props.addAnswer(null, props.i, props.j);
    setFileName('');

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * SUSPECTED DEFECT — `surveyImage` is dereferenced unguarded below. The `?.` on
   * `props` guarded nothing (a component parameter is never nullish); the index is
   * what throws. `questionnaire.tsx` passes `surveyImage={props?.surveyImage}` and
   * `item.tsx` forwards it, so a questionnaire rendered before any attachment has
   * been fetched supplies `undefined` and this crashes the render. Reachable only
   * once an answer carries a `valueReference`, which is why it has not been seen.
   * The assertions preserve the throw rather than converting it to a blank image.
   */
  const displayImage = (valueReference: Reference | undefined) => {
    const referenceId = valueReference?.reference?.split('/')?.[1];
    const surveyImage = props.surveyImage!;
    if (surveyImage[referenceId!]?.contentType !== 'application/pdf') {
      return (
        <div>
          <img
            key={referenceId}
            id={referenceId}
            width="100%"
            src={surveyImage[referenceId!]?.data}
            alt="survey attachment"
          />
        </div>
      );
    } else if (surveyImage[referenceId!]?.contentType === 'application/pdf') {
      // Asserted, not guarded: the branch is entered only when `contentType` matched on
      // this same lookup.
      const blob = base64ToPdfBlob(surveyImage[referenceId!]!.data);
      const url = URL.createObjectURL(blob);

      return (
        <div>
          <object key={referenceId}>
            <iframe
              title={'survey-pdf-' + referenceId}
              id={referenceId}
              width="100%"
              height="600"
              src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
            />
          </object>
        </div>
      );
    }
  };

  //display file selection
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  //if the question is disabled, the file input will be disabled.
  //if the question is enabled and we don't have a file in the current answer, then show upload document selection
  //if the question is enabled and we have a file in the current answer (most likely to update/switch a file), we will show a file preview after submission and display update document/delete document selection
  //currently only accept image and pdf file type
  return !props.enable ? (
    <Button icon labelPosition="left" disabled onClick={handleUploadClick}>
      <Icon name="upload" />
      Upload Document Here
    </Button>
  ) : props.currentAnswer?.length !== undefined && props.currentAnswer.length > 0 ? (
    <div>
      {error && <p style={{ color: 'red' }}>{error}</p>}

      {props.currentAnswer?.[0]?.valueReference &&
        displayImage(props.currentAnswer?.[0]?.valueReference)}

      <div>
        <Button icon labelPosition="left" onClick={handleUploadClick}>
          <Icon name="upload" />
          Update Document Here
        </Button>
        <Button icon labelPosition="left" onClick={handleDelete}>
          <Icon name="delete" />
          Delete
        </Button>

        {/* Raw `<input type="file">`: the design-system `TextInput`/`FormField` the rule
            points at render a text field, not a file picker. */}
        {/* eslint-disable-next-line no-restricted-syntax */}
        <input
          type="file"
          accept="image/*,application/pdf"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleChange}
        />
        {fileName && <p>Selected File: {fileName}</p>}
      </div>
    </div>
  ) : (
    <div>
      {error && <p style={{ color: 'red' }}>{error}</p>}

      <Button icon labelPosition="left" onClick={handleUploadClick}>
        <Icon name="upload" />
        Upload Document Here
      </Button>

      {/* Raw `<input type="file">`, as at the sibling site above. */}
      {/* eslint-disable-next-line no-restricted-syntax */}
      <input
        type="file"
        accept="image/*,application/pdf"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleChange}
      />
    </div>
  );
}
